import { Stack } from "@mui/material";
import { type UseModalState } from "@src/lib/ui-react";

import { Button } from "../components/Button";
import { Loader } from "../components/Loader";
import { Text } from "../components/Text";
import { AddLeadTimeDifferentialDialog } from "./components/AddLeadTimeDifferentialDialog";
import { LeadTimeDifferentialRow } from "./components/LeadTimeDifferentialRow";
import { formatLeadTimeRangeLabel } from "./constants";
import { type Differential } from "./types";

interface ShiftRushFeeScheduleViewProps {
  workplaceId: string;
  differentials: Differential[];
  isLoading: boolean;
  hasError: boolean;
  addModalState: UseModalState;
  isDeletingId: string | undefined;
  onAddClick: () => void;
  onDeleteClick: (differential: Differential) => void;
}

function ScheduleBody(props: {
  isLoading: boolean;
  hasError: boolean;
  differentials: Differential[];
  isDeletingId: string | undefined;
  onDeleteClick: (differential: Differential) => void;
}) {
  const { isLoading, hasError, differentials, isDeletingId, onDeleteClick } = props;

  if (isLoading) {
    return <Loader />;
  }

  if (hasError) {
    return (
      <Text sx={(theme) => ({ color: theme.palette.error.main })} variant="body2">
        Failed to load tiers. Try refreshing.
      </Text>
    );
  }

  if (differentials.length === 0) {
    return (
      <Text sx={(theme) => ({ color: theme.palette.text.secondary })} variant="body2">
        No tiers configured. Add one to start applying lead-time charge differentials.
      </Text>
    );
  }

  return (
    <Stack>
      {differentials.map((differential, index) => (
        <LeadTimeDifferentialRow
          key={differential.id}
          incrementalMultiplier={differential.incrementalMultiplier}
          isDeleting={isDeletingId === differential.id}
          label={formatLeadTimeRangeLabel({
            thresholdInHours: differential.thresholdInHours,
            previousThresholdInHours:
              index > 0 ? differentials[index - 1].thresholdInHours : undefined,
          })}
          onDeleteClick={() => {
            onDeleteClick(differential);
          }}
        />
      ))}
    </Stack>
  );
}

export function ShiftRushFeeScheduleView(props: ShiftRushFeeScheduleViewProps) {
  const {
    workplaceId,
    differentials,
    isLoading,
    hasError,
    addModalState,
    isDeletingId,
    onAddClick,
    onDeleteClick,
  } = props;

  return (
    <Stack spacing={3} sx={{ paddingY: 2 }}>
      <Stack
        direction="row"
        spacing={2}
        sx={{ alignItems: "center", justifyContent: "space-between" }}
      >
        <Text semibold variant="h6">
          Shift Rush Fee Schedule
        </Text>
        <Button size="small" variant="primary" onClick={onAddClick}>
          + Add tier
        </Button>
      </Stack>

      <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
        Shifts created within the threshold hours of start time will have the listed charge
        differential added on top of the Workplace Base Charge.
      </Text>

      <ScheduleBody
        differentials={differentials}
        hasError={hasError}
        isDeletingId={isDeletingId}
        isLoading={isLoading}
        onDeleteClick={onDeleteClick}
      />

      <AddLeadTimeDifferentialDialog
        existingDifferentials={differentials}
        modalState={addModalState}
        workplaceId={workplaceId}
      />
    </Stack>
  );
}
